import React, { useEffect, useState } from "react";

import {
  AlertTriangle,
  ClipboardList,
  Download,
  Loader2,
  PlusCircle,
  RotateCcw,
  Wallet,
} from "lucide-react";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent } from "~/components/ui/dialog";
import { ScrollArea } from "~/components/ui/scroll-area";
import { Sheet, SheetContent } from "~/components/ui/sheet";
import { FACTURA_OBSERVATIONS_MAX_LENGTH } from "~/constants/facturas";
import { toast } from "~/hooks/use-toast";
import { useRecoverRejectedSalesRequest } from "~/hooks/useRecoverRejectedSalesRequest";
import { useSalesConditions } from "~/hooks/useSalesConditions";
import { supabase } from "~/integrations/supabase/client";
import { useDispatchGuide } from "~/pages/operations/hooks/useDispatchGuide";
import { AddPaymentForm } from "~/pages/purchase-orders/components/order-detail/AddPaymentForm";
import { AddressEditSection } from "~/pages/purchase-orders/components/order-detail/AddressEditSection";
import { ApproveRejectActions } from "~/pages/purchase-orders/components/order-detail/ApproveRejectActions";
import { CreateDispatchGuideDialog } from "~/pages/purchase-orders/components/order-detail/CreateDispatchGuideDialog";
import { CreditPaymentActions } from "~/pages/purchase-orders/components/order-detail/CreditPaymentActions";
import {
  CreditUtilizationCard,
  CreditUtilizationCardSkeleton,
} from "~/pages/purchase-orders/components/order-detail/CreditUtilizationCard";
import { DeliveryDateInfo } from "~/pages/purchase-orders/components/order-detail/DeliveryDateInfo";
import { DispatchGuideLoadingSkeleton } from "~/pages/purchase-orders/components/order-detail/DispatchGuideLoadingSkeleton";
import { DispatchGuidesSection } from "~/pages/purchase-orders/components/order-detail/DispatchGuidesSection";
import DocumentsSection from "~/pages/purchase-orders/components/order-detail/DocumentsSection";
import { EditableNotesField } from "~/pages/purchase-orders/components/order-detail/EditableNotesField";
import { EnhancedTaxDocumentsSection } from "~/pages/purchase-orders/components/order-detail/EnhancedTaxDocumentsSection";
import { OrderDetailDialogSheets } from "~/pages/purchase-orders/components/order-detail/OrderDetailDialogSheets";
import { OrderDetailHeader } from "~/pages/purchase-orders/components/order-detail/OrderDetailHeader";
import { OrderItemsTable } from "~/pages/purchase-orders/components/order-detail/OrderItemsTable";
import { OrderSummaryCard } from "~/pages/purchase-orders/components/order-detail/OrderSummaryCard";
import { PaymentDetailsTable } from "~/pages/purchase-orders/components/order-detail/PaymentDetailsTable";
import { PrepaidApprovalActions } from "~/pages/purchase-orders/components/order-detail/PrepaidApprovalActions";
import { PricingApprovalActions } from "~/pages/purchase-orders/components/order-detail/PricingApprovalActions";
import { ProofOfDeliverySection } from "~/pages/purchase-orders/components/order-detail/ProofOfDeliverySection";
import { RevealGroup } from "~/pages/purchase-orders/components/order-detail/RevealOnScroll";
import { StatusActionButtons } from "~/pages/purchase-orders/components/order-detail/StatusActionButtons";
import { StatusHistoryTimeline } from "~/pages/purchase-orders/components/order-detail/StatusHistoryTimeline";
import { TelechequeIdCard } from "~/pages/purchase-orders/components/order-detail/TelechequeIdCard";
import { TransportistaRejectSheet } from "~/pages/purchase-orders/components/order-detail/TransportistaRejectSheet";
import { useClientOrderData } from "~/pages/purchase-orders/hooks/useClientOrderData";
import { useOrderActions } from "~/pages/purchase-orders/hooks/useOrderActions";
import { useOrderStatusHandlers } from "~/pages/purchase-orders/hooks/useOrderStatusHandlers";
import { usePayments } from "~/pages/purchase-orders/hooks/usePayments";
import { StatusHistoryRecord } from "~/pages/purchase-orders/hooks/useStatusHistory";
import { useTransportistaAssignment } from "~/pages/purchase-orders/hooks/useTransportistaAssignment";
import { ClientInfo } from "~/pages/purchase-orders/hooks/utils/clientUtils";
import { getStatusLabel } from "~/pages/purchase-orders/utils/statusLabels";
import { useProfileStore } from "~/store/profileStore";
import { SalesRequest, SalesRequestItem } from "~/types/client";
import { TransportData } from "~/types/dispatch";
import { formatCurrency } from "~/utils/helpers";
import { grossFromNet } from "~/utils/iva";
import { generatePaymentDocumentHtml } from "~/utils/paymentDocumentGenerator";
import { fetchAndTransformSalesRequest } from "~/utils/salesRequestTransform";
import { showErrorDialog } from "~/utils/swalHelpers";

interface OrderDialogContentProps {
  selectedOrder: SalesRequest;
  setSelectedOrder: (order: SalesRequest | null) => void;
  getSellerInfo: (sellerId: string) => {
    name: string;
    lastname: string;
    email: string;
  };
  getApproverRejecterInfo: (approverId?: string) => {
    name: string;
    lastname: string;
    email: string;
    role: string;
  };
  clientNames: Record<string, string>;
  items: SalesRequestItem[];
  onOrderStatusChange?: () => void;
  isApproveSheetOpen: boolean;
  setIsApproveSheetOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isRejectSheetOpen: boolean;
  setIsRejectSheetOpen: React.Dispatch<React.SetStateAction<boolean>>;
  clientRiskChecked?: boolean;
  clientRiskStatus?: Record<string, boolean>;
  clientCreditData?: {
    utilized: number;
    available: number;
    percentage: number;
    insuredAmount: number;
  };
  creditLoading?: boolean;
  canChangeSeller?: boolean;
  sellers?: { id: string; name: string; lastname: string }[];
  selectedSellerId?: string;
  handleSellerChange?: (sellerId: string) => void;
  isSubmitting?: boolean;
  statusHistory?: StatusHistoryRecord[];
  loadingStatusHistory?: boolean;
  usersById?: Record<string, { name: string; lastname: string; email: string }>;
  useDrawer?: boolean;
  clientInfo?: Record<string, ClientInfo>;
}

/**
 * OrderDialogContent component that displays sales request details
 *
 * Issues fixed:
 * - Added type assertion for client data to safely access sales_condition_id
 * - Removed explicit :any type annotations on error handling
 * - Made sure OrderDetailDialogSheets props match its interface definition
 */
export const OrderDialogContent: React.FC<OrderDialogContentProps> = ({
  selectedOrder,
  setSelectedOrder,
  getSellerInfo,
  getApproverRejecterInfo,
  clientNames,
  items,
  onOrderStatusChange,
  isApproveSheetOpen,
  setIsApproveSheetOpen,
  isRejectSheetOpen,
  setIsRejectSheetOpen,
  clientRiskChecked = true,
  clientRiskStatus,
  clientCreditData,
  creditLoading = false,
  canChangeSeller = false,
  sellers = [],
  selectedSellerId,
  handleSellerChange = () => {},
  isSubmitting = false,
  statusHistory = [],
  loadingStatusHistory = false,
  usersById = {},
  useDrawer = false,
  clientInfo = {},
}) => {
  const seller = getSellerInfo(selectedOrder.createdBy);
  const approverRejecter = getApproverRejecterInfo(
    selectedOrder.approvedRejectedBy
  );
  const [isCopied, setIsCopied] = useState(false);
  const [isGeneratingPaymentDoc, setIsGeneratingPaymentDoc] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [transportistaRejectionReason, setTransportistaRejectionReason] =
    useState("");
  const [isTransportistaRejectSheetOpen, setIsTransportistaRejectSheetOpen] =
    useState(false);
  const [showManualPaymentForm, setShowManualPaymentForm] = useState(false);

  // Recovery hook for rejected sales requests
  const { recoverSalesRequest, canRecoverSalesRequest, isRecovering } =
    useRecoverRejectedSalesRequest();

  // Reset rejection form when sheets are closed
  useEffect(() => {
    if (!isRejectSheetOpen) {
      setRejectionReason("");
      setCustomReason("");
    }
  }, [isRejectSheetOpen]);

  // Reset transportista rejection form when sheet is closed
  useEffect(() => {
    if (!isTransportistaRejectSheetOpen) {
      setTransportistaRejectionReason("");
    }
  }, [isTransportistaRejectSheetOpen]);

  // Client data hook for tax_id, sales_condition_id, and internal_notes
  const {
    clientTaxId,
    clientSalesConditionId,
    clientInternalNotes,
    isLoading: loadingClientData,
  } = useClientOrderData(selectedOrder.clientId);

  const [refreshKey, setRefreshKey] = useState(0);
  const [localItems, setLocalItems] = useState<SalesRequestItem[]>(items);

  // Sync local items with incoming items prop
  useEffect(() => {
    setLocalItems(items);
  }, [items]);

  const profileStore = useProfileStore();

  // Payments card: prepaid/unpaid-accepted orders embed the payment form
  // directly; otherwise the table renders with a manual-registration action
  // in the card header.
  const showsInlinePaymentForm =
    (profileStore.role === "seller" && selectedOrder.status === "prepaid") ||
    (profileStore.role !== "seller" &&
      (selectedOrder.status === "prepaid" ||
        (selectedOrder.status === "accepted" && !selectedOrder.paid)));
  const canRegisterManualPayment =
    profileStore.role !== "transportista" && profileStore.role !== "logistica";

  // Transportista assignment hook
  const {
    transportistas,
    assignedTransportista,
    isLoading: isLoadingTransportistas,
    isAssigning: isAssigningTransportista,
    handleTransportistaChange,
  } = useTransportistaAssignment({
    salesRequestId: selectedOrder.id,
    initialTransportistaId: selectedOrder.assigned_transportista,
    userRole: profileStore.role,
    onOrderStatusChange,
  });
  const {
    payments,
    loading: paymentsLoading,
    refresh: refreshPayments,
  } = usePayments(selectedOrder.id);
  const { handleApprove, handleReject } = useOrderStatusHandlers(
    selectedOrder,
    rejectionReason,
    onOrderStatusChange,
    setIsApproveSheetOpen,
    setIsRejectSheetOpen,
    customReason,
    () => setSelectedOrder(null)
  );

  // Order actions hook for status changes
  const {
    markAsTest,
    markAsDelivered,
    revertToInTransit,
    markAsPaid,
    markAsUnpaid,
    handleForceApproveWithoutPaymentValidation,
    handleTransportistaReject: handleTransportistaRejectAction,
    approveDocument,
    isProcessing: isActionProcessing,
  } = useOrderActions({
    selectedOrder,
    setSelectedOrder,
    onOrderStatusChange,
  });

  // Handle recovery of rejected sales requests
  const handleRecoverRejected = async () => {
    const success = await recoverSalesRequest(selectedOrder, () => {
      // Refresh the order data after successful recovery
      if (onOrderStatusChange) {
        onOrderStatusChange();
      }
    });

    if (success) {
      // Close the dialog since the order status has changed
      setSelectedOrder(null);
    }
  };

  // Wrapper for transportista rejection that uses local state
  const handleTransportistaReject = async () => {
    await handleTransportistaRejectAction(transportistaRejectionReason.trim());
    setIsTransportistaRejectSheetOpen(false);
    setTransportistaRejectionReason("");
  };

  const canApproveOrReject =
    profileStore.role === "superadmin" ||
    profileStore.role === "logistica" ||
    (profileStore.role === "cobranza" &&
      (selectedOrder.status === "pending" ||
        selectedOrder.status === "payment_to_check"));

  // Check if user is admin (can mark as test)
  const isAdmin =
    profileStore.role === "superadmin" || profileStore.role === "admin";

  // Check if user can approve documents (move accepted sales notes to "por_facturar")
  const canApproveDocument =
    profileStore.role === "superadmin" ||
    profileStore.role === "admin" ||
    profileStore.role === "facturacion";

  // Check if user can mark orders as paid (includes cobranza for payment validation)
  const canMarkAsPaid =
    profileStore.role === "superadmin" ||
    profileStore.role === "admin" ||
    profileStore.role === "cobranza";

  // Only superadmin and cobranza users can modify sales conditions
  // pending_credit orders can change to prepaid condition to resolve the credit insufficiency
  const canModifySettings =
    (profileStore.role === "superadmin" || profileStore.role === "cobranza") &&
    (selectedOrder.status === "pending" ||
      selectedOrder.status === "pending_credit");

  const exceedsAvailableCredit =
    clientCreditData &&
    (selectedOrder.price_with_iva || selectedOrder.totalAmount) >
      clientCreditData.available;

  const isPaid = selectedOrder.paid;

  const { salesConditions, getConditionById } = useSalesConditions();
  const [isEditingSalesCondition, setIsEditingSalesCondition] = useState(false);
  const [savingCondition, setSavingCondition] = useState(false);
  const [selectedSalesConditionId, setSelectedSalesConditionId] = useState<
    string | undefined
  >(selectedOrder.salesConditionId || undefined);

  // Payment terms editing state
  const [isEditingPaymentTerms, setIsEditingPaymentTerms] = useState(false);
  const [savingPaymentTerms, setSavingPaymentTerms] = useState(false);
  const [paymentTerms, setPaymentTerms] = useState(selectedOrder.paymentTerms);
  const [paymentType, setPaymentType] = useState<"future" | "prepaid">(
    selectedOrder.paymentType || "future"
  );

  // Add state variables for editing internal notes
  const [isEditingInternalNotes, setIsEditingInternalNotes] = useState(false);
  const [editedInternalNotes, setEditedInternalNotes] = useState("");
  const [savingInternalNotes, setSavingInternalNotes] = useState(false);

  // Add state variables for factura_observations
  const [isEditingFacturaObservations, setIsEditingFacturaObservations] =
    useState(false);
  const [editedFacturaObservations, setEditedFacturaObservations] =
    useState("");
  const [savingFacturaObservations, setSavingFacturaObservations] =
    useState(false);

  // Add state variables for editing telecheque_id
  const [isEditingTelechequeId, setIsEditingTelechequeId] = useState(false);
  const [editedTelechequeId, setEditedTelechequeId] = useState("");
  const [savingTelechequeId, setSavingTelechequeId] = useState(false);

  // Add state for dispatch guide creation dialog
  const [showCreateDispatchGuideDialog, setShowCreateDispatchGuideDialog] =
    useState(false);

  // Add dispatch guide functionality
  const { isCreatingDispatchGuide, createDispatchGuide } = useDispatchGuide({
    refreshPageWithParams: true,
  });

  // Handler for dispatch guide creation with transport data + observations
  const handleCreateDispatchGuide = async (
    transport: TransportData,
    observations: string
  ) => {
    try {
      await createDispatchGuide(selectedOrder, transport, observations);
      // Only close dialog on successful creation
      setShowCreateDispatchGuideDialog(false);
    } catch (error) {
      // Don't close dialog on error - let user see the error and try again
      console.error("Failed to create dispatch guide:", error);
    }
  };

  // Handler for opening dispatch guide dialog
  const handleOpenDispatchGuideDialog = () => {
    setShowCreateDispatchGuideDialog(true);
  };

  // Check if dispatch guide can be created (no existing guides + valid status)
  const canCreateDispatchGuide = (order: SalesRequest) => {
    return (
      !["rejected", "test", "in_transit", "delivered"].includes(order.status) &&
      existingDispatchGuides.length === 0
    );
  };

  // Check if this sales request uses TELECHEQUE 30 DIAS condition
  const TELECHEQUE_CONDITION_ID = "d1d384ec-12d7-4e38-baa2-0b943adc8d6c";

  // State to track existing dispatch guides
  const [existingDispatchGuides, setExistingDispatchGuides] = useState<any[]>(
    []
  );
  // Whether a non-cancelled factura has already been issued for this order.
  // Used to lock line-item editing so the order can't drift away from the
  // issued invoice (which would corrupt the BI/Excel export and inflate debt).
  const [hasActiveFactura, setHasActiveFactura] = useState(false);

  // Check if order can be modified (dispatch_ready status and no existing guides)
  const canModifyDispatchOrder =
    selectedOrder.status === "dispatch_ready" &&
    existingDispatchGuides.length === 0;

  const hasTelechequeCondition =
    selectedOrder.salesConditionId === TELECHEQUE_CONDITION_ID ||
    clientSalesConditionId === TELECHEQUE_CONDITION_ID;
  const shouldShowTelechequeInput =
    hasTelechequeCondition && selectedOrder.status === "pending";

  // Fetch existing dispatch guides to prevent modification when guides exist
  useEffect(() => {
    const fetchExistingDispatchGuides = async () => {
      try {
        const { data, error } = await supabase
          .from("sales_request_documents")
          .select("*")
          .eq("sales_request_id", selectedOrder.id)
          .eq("document_type", "guia_de_despacho")
          .or("cancelled.is.null,cancelled.eq.false"); // Only non-cancelled guides

        if (!error && data) {
          setExistingDispatchGuides(data);
        }
      } catch (error) {
        console.error("Error fetching existing dispatch guides:", error);
      }
    };

    // Check whether a non-cancelled factura already exists. If so, line items
    // must be locked so the order can't diverge from the issued invoice.
    const fetchActiveFactura = async () => {
      try {
        const { data, error } = await supabase
          .from("sales_request_documents")
          .select("id")
          .eq("sales_request_id", selectedOrder.id)
          .eq("document_type", "factura")
          .not("bsale_number", "is", null)
          .or("cancelled.is.null,cancelled.eq.false") // Only non-cancelled facturas
          .limit(1);

        if (!error) {
          setHasActiveFactura((data?.length ?? 0) > 0);
        }
      } catch (error) {
        console.error("Error fetching active factura:", error);
      }
    };

    fetchExistingDispatchGuides();
    fetchActiveFactura();
  }, [selectedOrder.id]);

  useEffect(() => {
    if (selectedOrder.internal_notes) {
      setEditedInternalNotes(selectedOrder.internal_notes);
    } else {
      setEditedInternalNotes("");
    }

    if (selectedOrder.factura_observations) {
      setEditedFacturaObservations(selectedOrder.factura_observations);
    } else {
      setEditedFacturaObservations("");
    }

    if (selectedOrder.telecheque_id) {
      setEditedTelechequeId(selectedOrder.telecheque_id);
    } else {
      setEditedTelechequeId("");
    }
  }, [
    selectedOrder.internal_notes,
    selectedOrder.factura_observations,
    selectedOrder.telecheque_id,
  ]);

  const updateSalesCondition = async () => {
    if (!canModifySettings) {
      toast({
        title: "Permiso denegado",
        description: !["pending", "pending_credit"].includes(
          selectedOrder.status
        )
          ? "Solo se pueden modificar las condiciones de venta para notas en estado 'pendiente' o 'crédito insuficiente'."
          : "No tienes permisos para cambiar la condición de venta de este pedido.",
        variant: "destructive",
      });
      return;
    }

    setSavingCondition(true);
    try {
      // If "inherit" is selected or undefined, set to null to inherit from client
      const valueToSave =
        selectedSalesConditionId === "inherit" ||
        selectedSalesConditionId === undefined
          ? null
          : selectedSalesConditionId;

      const { error } = await supabase
        .from("sales_request")
        .update({
          sales_condition_id: valueToSave,
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedOrder.id);

      if (error) throw error;

      // Check if we need to transition status from pending_credit to prepaid
      // This happens when changing to a prepaid condition on an order with insufficient credit
      const newCondition = valueToSave ? getConditionById(valueToSave) : null;
      const isPrepaidCondition =
        newCondition?.type === "prepaid" ||
        newCondition?.requiresPrepayment === true;

      if (selectedOrder.status === "pending_credit" && isPrepaidCondition) {
        // Update status to prepaid since prepaid orders don't require credit validation
        const { error: statusError } = await supabase
          .from("sales_request")
          .update({ status: "prepaid", updated_at: new Date().toISOString() })
          .eq("id", selectedOrder.id);

        if (statusError) throw statusError;

        // Trigger logs the change; just attach the reason to that row.
        await supabase.rpc("annotate_latest_status_change", {
          p_sales_request_id: selectedOrder.id,
          p_new_status: "prepaid",
          p_comment: "Condición de venta cambiada a prepago",
        });
      }

      // Refresh the sales request data
      const updatedOrder = await fetchAndTransformSalesRequest(
        selectedOrder.id
      );
      if (updatedOrder) {
        setSelectedOrder(updatedOrder);
      }

      // Determine which condition was applied for the success message
      const appliedCondition = valueToSave
        ? getConditionById(valueToSave)?.name
        : clientSalesConditionId
          ? `${getConditionById(clientSalesConditionId)?.name} (del cliente)`
          : "Sin condición específica";

      toast({
        title: "Condición actualizada",
        description: valueToSave
          ? "Se ha aplicado una condición específica para esta nota de venta."
          : "La nota de venta ahora usará la condición asociada al cliente.",
      });

      setIsEditingSalesCondition(false);

      // Dispatch client-updated event to refresh client details if someone is viewing that page
      window.dispatchEvent(
        new CustomEvent("client-updated", {
          detail: { clientId: selectedOrder.clientId },
        })
      );

      if (onOrderStatusChange) await onOrderStatusChange();
    } catch (error) {
      // Use a more specific error type instead of any
      console.error("Error updating sales condition:", error);
      toast({
        title: "Error",
        description: "No se pudo actualizar la condición de venta.",
        variant: "destructive",
      });
    } finally {
      setSavingCondition(false);
    }
  };

  const updatePaymentTerms = async () => {
    if (!canModifySettings) {
      toast({
        title: "Permiso denegado",
        description: !["pending", "pending_credit"].includes(
          selectedOrder.status
        )
          ? "Solo se pueden modificar los términos de pago para notas en estado 'pendiente' o 'crédito insuficiente'."
          : "No tienes permisos para cambiar los términos de pago de este pedido.",
        variant: "destructive",
      });
      return;
    }

    setSavingPaymentTerms(true);
    try {
      const { error } = await supabase
        .from("sales_request")
        .update({
          payment_terms: paymentTerms,
          payment_type: paymentType,
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedOrder.id);

      if (error) throw error;

      // Refresh the sales request data
      const updatedOrder = await fetchAndTransformSalesRequest(
        selectedOrder.id
      );
      if (updatedOrder) {
        setSelectedOrder(updatedOrder);
      }

      toast({
        title: "Términos de pago actualizados",
        description: "Los términos de pago han sido actualizados exitosamente.",
      });

      setIsEditingPaymentTerms(false);
      if (onOrderStatusChange) await onOrderStatusChange();
    } catch (error) {
      // Use a more specific error type instead of any
      console.error("Error updating payment terms:", error);
      toast({
        title: "Error",
        description: "No se pudieron actualizar los términos de pago.",
        variant: "destructive",
      });
    } finally {
      setSavingPaymentTerms(false);
    }
  };

  const updateInternalNotes = async () => {
    // Check if the current user is the one who created the sales request or has facturacion/superadmin role
    // Also allow cobranza role when status is payment_to_check (Validación de Pago)
    const isCreator = profileStore.id === selectedOrder.createdBy;
    const isSuperadmin = profileStore.role === "superadmin";
    const isFacturacion = profileStore.role === "facturacion";
    const isCobranzaWithPaymentValidation =
      profileStore.role === "cobranza" &&
      selectedOrder.status === "payment_to_check";

    if (
      !isCreator &&
      !isSuperadmin &&
      !isFacturacion &&
      !isCobranzaWithPaymentValidation
    ) {
      toast({
        title: "Permiso denegado",
        description:
          "Solo el vendedor que creó esta nota, superadmin, usuarios con rol de facturación, o usuarios de cobranza durante validación de pago pueden editar las notas internas.",
        variant: "destructive",
      });
      return;
    }

    // Internal notes can't be erased once written: block emptying existing notes.
    const hasExistingNotes = (selectedOrder.internal_notes ?? "").trim() !== "";
    if (hasExistingNotes && editedInternalNotes.trim() === "") {
      toast({
        title: "Acción no permitida",
        description:
          "Las notas internas no se pueden borrar una vez escritas.",
        variant: "destructive",
      });
      return;
    }

    setSavingInternalNotes(true);
    try {
      const { error } = await supabase
        .from("sales_request")
        .update({
          internal_notes: editedInternalNotes,
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedOrder.id);

      if (error) throw error;

      // Refresh the sales request data
      const updatedOrder = await fetchAndTransformSalesRequest(
        selectedOrder.id
      );
      if (updatedOrder) {
        setSelectedOrder(updatedOrder);
      }

      toast({
        title: "Notas internas actualizadas",
        description: "Las notas internas han sido actualizadas correctamente.",
      });

      setIsEditingInternalNotes(false);

      if (onOrderStatusChange) await onOrderStatusChange();
    } catch (error) {
      console.error("Error updating internal notes:", error);
      toast({
        title: "Error",
        description: "No se pudieron actualizar las notas internas.",
        variant: "destructive",
      });
    } finally {
      setSavingInternalNotes(false);
    }
  };

  const updateFacturaObservations = async () => {
    // This function can be used by any user with access to the drawer
    setSavingFacturaObservations(true);
    try {
      const { error } = await supabase
        .from("sales_request")
        .update({
          factura_observations: editedFacturaObservations,
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedOrder.id);

      if (error) throw error;

      // Refresh the sales request data
      const updatedOrder = await fetchAndTransformSalesRequest(
        selectedOrder.id
      );
      if (updatedOrder) {
        setSelectedOrder(updatedOrder);
      }

      toast({
        title: "Observaciones de factura actualizadas",
        description:
          "Las observaciones de factura han sido actualizadas correctamente.",
      });

      setIsEditingFacturaObservations(false);

      if (onOrderStatusChange) await onOrderStatusChange();
    } catch (error) {
      console.error("Error updating factura observations:", error);
      toast({
        title: "Error",
        description: "No se pudieron actualizar las observaciones de factura.",
        variant: "destructive",
      });
    } finally {
      setSavingFacturaObservations(false);
    }
  };

  const updateTelechequeId = async () => {
    setSavingTelechequeId(true);
    try {
      const { error } = await supabase
        .from("sales_request")
        .update({
          telecheque_id: editedTelechequeId,
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedOrder.id);

      if (error) throw error;

      // Refresh the sales request data
      const updatedOrder = await fetchAndTransformSalesRequest(
        selectedOrder.id
      );
      if (updatedOrder) {
        setSelectedOrder(updatedOrder);
      }

      toast({
        title: "ID de Telecheque actualizado",
        description: "El ID de Telecheque ha sido actualizado correctamente.",
      });

      setIsEditingTelechequeId(false);

      if (onOrderStatusChange) await onOrderStatusChange();
    } catch (error) {
      console.error("Error updating telecheque_id:", error);
      toast({
        title: "Error",
        description: "No se pudo actualizar el ID de Telecheque.",
        variant: "destructive",
      });
    } finally {
      setSavingTelechequeId(false);
    }
  };

  const effectiveSalesConditionId =
    selectedOrder.salesConditionId || clientSalesConditionId;
  const effectiveSalesCondition = getConditionById(effectiveSalesConditionId);

  // Check if prepayment is required based on sales condition
  const requiresPrepayment =
    effectiveSalesCondition?.requiresPrepayment ||
    effectiveSalesCondition?.type === "prepaid" ||
    effectiveSalesCondition?.name === "ANTICIPADO";

  // Check if this is TRANSFERENCIA ANTICIPADA condition (should show payment document)
  const isTransferenciaAnticipada =
    effectiveSalesCondition?.name === "TRANSFERENCIA ANTICIPADA" ||
    effectiveSalesCondition?.name
      ?.toLowerCase()
      .includes("transferencia anticipada") ||
    requiresPrepayment;

  // Show warning if prepayment is required but order is not paid
  const showPrepaymentWarning =
    requiresPrepayment && !isPaid && selectedOrder.status !== "rejected";

  // Determine effective payment terms from sales condition
  const getSalesConditionPaymentTerms = () => {
    if (!effectiveSalesCondition) return null;

    const defaultTerms = {
      days: effectiveSalesCondition.maximumDays || 30,
      type:
        effectiveSalesCondition.type === "prepaid"
          ? "prepaid"
          : ("future" as "prepaid" | "future"),
    };

    return defaultTerms;
  };

  const conditionPaymentTerms = getSalesConditionPaymentTerms();

  // Determine if payment terms are inherited or overridden
  const isPaymentTermsOverridden =
    (conditionPaymentTerms && paymentTerms !== conditionPaymentTerms.days) ||
    (conditionPaymentTerms && paymentType !== conditionPaymentTerms.type);

  const generatePaymentDocument = async () => {
    setIsGeneratingPaymentDoc(true);
    try {
      // Use our HTML-based document generator
      const result = await generatePaymentDocumentHtml(
        selectedOrder,
        clientNames[selectedOrder.clientId],
        seller,
        items
      );

      if (result === true) {
        toast({
          title: "Documento generado",
          description:
            "El documento de pago se ha abierto en una nueva ventana.",
        });
      } else if (result === "download") {
        toast({
          title: "Documento descargado",
          description:
            "El navegador bloqueó la ventana. El documento se descargó a tu dispositivo.",
        });
      } else {
        throw new Error("No se pudo generar el documento de pago.");
      }
    } catch (error) {
      console.error("Error generating payment document:", error);
      toast({
        title: "Error",
        description:
          "No se pudo generar el documento de pago. " +
          (error instanceof Error ? error.message : ""),
        variant: "destructive",
      });

      // Show error details in a dialog for debugging
      showErrorDialog(
        "Error al generar el documento",
        error instanceof Error ? error.message : String(error)
      );
    } finally {
      setIsGeneratingPaymentDoc(false);
    }
  };

  // Common content that will be used in both dialog and sheet
  const renderContent = () => {
    // Show skeleton loading state when creating dispatch guide
    if (isCreatingDispatchGuide === selectedOrder.id) {
      return <DispatchGuideLoadingSkeleton />;
    }

    // Show normal content
    return (
      <div className="space-y-6">
        <OrderDetailHeader
          selectedOrder={selectedOrder}
          isCopied={isCopied}
          setIsCopied={setIsCopied}
          canCreateDispatchGuide={canCreateDispatchGuide(selectedOrder)}
          onCreateDispatchGuide={handleOpenDispatchGuideDialog}
          isCreatingDispatchGuide={isCreatingDispatchGuide === selectedOrder.id}
        />

        <ScrollArea className="flex-1 overflow-y-auto pr-4">
          <RevealGroup className="grid min-w-0 grid-cols-1 gap-6 py-4">
            {/* Order Summary Card */}
            <OrderSummaryCard
              selectedOrder={selectedOrder}
              clientName={clientNames[selectedOrder.clientId]}
              clientTaxId={clientTaxId}
              seller={seller}
              canChangeSeller={canChangeSeller}
              selectedSellerId={selectedSellerId}
              handleSellerChange={handleSellerChange}
              isSubmitting={isSubmitting}
              sellers={sellers}
              userRole={profileStore.role}
              transportistas={transportistas}
              assignedTransportista={assignedTransportista}
              handleTransportistaChange={handleTransportistaChange}
              isAssigningTransportista={isAssigningTransportista}
              isLoadingTransportistas={isLoadingTransportistas}
              clientInfo={clientInfo}
              clientInternalNotes={clientInternalNotes}
              effectiveSalesCondition={effectiveSalesCondition}
              clientSalesConditionId={clientSalesConditionId}
              canModifySettings={canModifySettings}
              salesConditions={salesConditions}
              isEditingSalesCondition={isEditingSalesCondition}
              setIsEditingSalesCondition={setIsEditingSalesCondition}
              selectedSalesConditionId={selectedSalesConditionId}
              setSelectedSalesConditionId={setSelectedSalesConditionId}
              savingSalesCondition={savingCondition}
              onSaveSalesCondition={updateSalesCondition}
            />

            {/* Rejection Motive Section - Only show for rejected orders */}
            {selectedOrder.status === "rejected" &&
              selectedOrder.rejectedReason && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-5 shadow-sm">
                  <h3 className="mb-3 flex items-center text-lg font-semibold text-red-900">
                    <AlertTriangle className="mr-2 h-5 w-5 text-red-600" />
                    Motivo de Rechazo
                  </h3>
                  <div className="rounded-lg border border-red-100 bg-white/50 p-3">
                    <p className="whitespace-pre-wrap text-sm text-red-800">
                      {selectedOrder.rejectedReason}
                    </p>
                  </div>

                  {/* Recovery button for rejected sales notes */}
                  {selectedOrder.previousStatus && (
                    <div className="mt-4 flex justify-center">
                      <Button
                        onClick={handleRecoverRejected}
                        disabled={isRecovering}
                        variant="outline"
                        className="h-9 gap-2"
                        size="sm"
                      >
                        <RotateCcw className="h-4 w-4" />
                        {isRecovering
                          ? "Recuperando..."
                          : `Recuperar a: ${getStatusLabel(selectedOrder.previousStatus)}`}
                      </Button>
                    </div>
                  )}
                </div>
              )}

            {showPrepaymentWarning && (
              <Alert className="border-amber-200 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <AlertDescription className="text-amber-800">
                  Esta nota de venta requiere pago anticipado antes de proceder
                  al despacho.
                  {selectedOrder.status === "accepted" &&
                    " El pedido está aprobado pero no podrá ser despachado hasta recibir el pago."}
                </AlertDescription>
              </Alert>
            )}

            {/* Manual approval for prepaid sales requests */}
            <PrepaidApprovalActions
              selectedOrder={selectedOrder}
              clientName={clientNames[selectedOrder.clientId]}
              onOrderStatusChange={onOrderStatusChange}
            />

            {/* Credit payment actions for sellers with credit condition clients */}
            <CreditPaymentActions
              selectedOrder={selectedOrder}
              clientInfo={clientInfo[selectedOrder.clientId]}
              onOrderStatusChange={onOrderStatusChange}
            />

            {/* Download payment document button for prepaid status or TRANSFERENCIA ANTICIPADA */}
            {(selectedOrder.status === "prepaid" ||
              isTransferenciaAnticipada) && (
              <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
                <div className="border-b border-gray-200 bg-gray-100 px-5 py-3">
                  <h3 className="flex items-center text-lg font-semibold text-gray-800">
                    <Download className="mr-2 h-5 w-5 text-gray-600" />
                    Documento de Pago
                  </h3>
                </div>
                <div className="p-5">
                  <p className="mb-4 text-sm text-muted-foreground">
                    Descargue el documento de pago para realizar la
                    transferencia. Una vez realizado el pago, por favor contacte
                    a su representante de ventas.
                  </p>
                  <Button
                    onClick={() => {
                      generatePaymentDocument().catch((error) => {
                        console.error(
                          "Error handling payment document generation:",
                          error
                        );
                      });
                    }}
                    size="sm"
                    className="w-full"
                    disabled={isGeneratingPaymentDoc}
                  >
                    {isGeneratingPaymentDoc ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="mr-2 h-4 w-4" />
                    )}
                    {isGeneratingPaymentDoc
                      ? "Generando documento..."
                      : "Descargar documento de pago"}
                  </Button>
                </div>
              </div>
            )}

            {/* Payment & Credit Section */}
            {profileStore.role !== "transportista" &&
              !isPaid &&
              selectedOrder.status !== "prepaid" &&
              creditLoading &&
              !clientCreditData && <CreditUtilizationCardSkeleton />}
            {profileStore.role !== "transportista" &&
              clientCreditData &&
              !isPaid &&
              selectedOrder.status !== "prepaid" &&
              !(clientCreditData.insuredAmount === 0) && (
                <CreditUtilizationCard
                  clientCreditData={clientCreditData}
                  orderAmount={selectedOrder.totalAmount}
                  orderAmountWithIva={grossFromNet(
                    selectedOrder.totalAmount,
                    selectedOrder.isExento
                  )}
                  exceedsAvailableCredit={exceedsAvailableCredit}
                />
              )}

            {/* Items Section */}
            <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
              <OrderItemsTable
                items={localItems}
                editable={
                  // Products are editable BEFORE factura generation (before status becomes "accepted")
                  // and while no dispatch guide has been created for statuses that would prevent editing.
                  // Also lock once a factura has been issued: editing then would make the order
                  // diverge from the invoice, corrupting the BI/Excel export and inflating debt.
                  !hasActiveFactura &&
                  [
                    "pending",
                    "pending_pricing",
                    "pending_credit",
                    "prepaid",
                    "payment_to_check",
                    "dispatch_ready",
                  ].includes(selectedOrder.status)
                }
                salesRequestId={selectedOrder.id}
                orderStatus={selectedOrder.status}
                onItemsChange={(
                  updatedItems,
                  newTotalAmount,
                  newPriceWithIva
                ) => {
                  // Update local items state to show changes immediately
                  setLocalItems(updatedItems);

                  // Update the selectedOrder with new totals
                  if (
                    newTotalAmount !== undefined &&
                    newPriceWithIva !== undefined
                  ) {
                    selectedOrder.totalAmount = newTotalAmount;
                    selectedOrder.price_with_iva = newPriceWithIva;

                    // Trigger a re-render to show updated totals immediately
                    setRefreshKey((prev) => prev + 1);

                    // Emit event for other components that might need to refresh
                    window.dispatchEvent(
                      new CustomEvent("sales-request-updated", {
                        detail: {
                          salesRequestId: selectedOrder.id,
                          newTotalAmount,
                          newPriceWithIva,
                        },
                      })
                    );
                  }
                }}
              />
              <div className="border-t border-gray-200 bg-gray-50 px-5 py-4">
                <div className="flex flex-col space-y-1">
                  {selectedOrder.isExento ? (
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-gray-800">
                        Total (exento):
                      </span>
                      <span className="text-lg font-bold text-gray-800">
                        {formatCurrency(selectedOrder.totalAmount)}
                      </span>
                    </div>
                  ) : selectedOrder.price_with_iva ? (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Neto:</span>
                        <span className="text-sm text-gray-600">
                          {formatCurrency(selectedOrder.totalAmount)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">
                          IVA (19%):
                        </span>
                        <span className="text-sm text-gray-600">
                          {formatCurrency(
                            selectedOrder.price_with_iva -
                              selectedOrder.totalAmount
                          )}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center justify-between border-t border-gray-200 pt-2">
                        <span className="text-lg font-bold text-gray-800">
                          Total con IVA:
                        </span>
                        <span className="text-lg font-bold text-gray-800">
                          {formatCurrency(selectedOrder.price_with_iva)}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-gray-800">
                        Total con IVA (19%):
                      </span>
                      <span className="text-lg font-bold text-gray-800">
                        {formatCurrency(
                          Math.round(selectedOrder.totalAmount * 1.19)
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Documents Section */}
            <DocumentsSection
              salesRequestId={selectedOrder.id}
              isEditable={true}
              orderStatus={selectedOrder.status}
            />

            {/* Dispatch Guides Section */}
            <DispatchGuidesSection
              salesRequestId={selectedOrder.id}
              orderStatus={selectedOrder.status}
              selectedOrder={selectedOrder}
              onOrderStatusChange={onOrderStatusChange}
            />

            {/* Address Edit Section */}
            <AddressEditSection
              selectedOrder={selectedOrder}
              onOrderUpdate={async () => {
                if (onOrderStatusChange) await onOrderStatusChange();
              }}
            />

            {/* Tax Documents Section */}
            {profileStore.role !== "transportista" && (
              <EnhancedTaxDocumentsSection
                salesRequestId={selectedOrder.id}
                orderStatus={selectedOrder.status}
              />
            )}

            {/* Proof of Delivery Section - Only for in_transit orders */}
            <ProofOfDeliverySection
              selectedOrder={selectedOrder}
              onOrderStatusChange={onOrderStatusChange}
            />

            {/* Payments Section */}
            {(payments?.length > 0 || !paymentsLoading) && (
              <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
                <div className="flex items-center justify-between border-b border-gray-200 bg-gray-100 px-5 py-3">
                  <h3 className="flex items-center text-lg font-semibold text-gray-800">
                    <Wallet className="mr-2 h-5 w-5 text-gray-600" />
                    Pagos
                  </h3>
                  {!showsInlinePaymentForm &&
                    canRegisterManualPayment &&
                    !showManualPaymentForm && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowManualPaymentForm(true)}
                      >
                        <PlusCircle className="mr-2 h-4 w-4" />
                        Registrar pago manual
                      </Button>
                    )}
                </div>

                {/* Show either the AddPaymentForm (which has its own table) or the PaymentDetailsTable */}
                {/* For seller users, only show AddPaymentForm for prepaid status */}
                {/* For other users, show AddPaymentForm for prepaid or accepted (not paid) status */}
                {showsInlinePaymentForm ? (
                  <AddPaymentForm
                    selectedOrder={selectedOrder}
                    onPaymentAdded={async () => {
                      refreshPayments();
                      if (onOrderStatusChange) await onOrderStatusChange();
                    }}
                    totalAmount={grossFromNet(
                      selectedOrder.totalAmount,
                      selectedOrder.isExento
                    )}
                    currentPayments={payments}
                    onCloseOrderDialog={() => setSelectedOrder(null)}
                  />
                ) : (
                  <div className="p-0">
                    <PaymentDetailsTable
                      payments={payments}
                      loading={paymentsLoading}
                      salesRequest={selectedOrder}
                      onPaymentAdded={async () => {
                        refreshPayments();
                        if (onOrderStatusChange) await onOrderStatusChange();
                      }}
                      clientInfo={clientInfo[selectedOrder.clientId]}
                      showPaymentForm={showManualPaymentForm}
                      setShowPaymentForm={setShowManualPaymentForm}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Status History Timeline */}
            {profileStore.role !== "transportista" && (
              <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
                <div className="border-b border-gray-200 bg-gray-100 px-5 py-3">
                  <h3 className="flex items-center text-lg font-semibold text-gray-800">
                    <ClipboardList className="mr-2 h-5 w-5 text-gray-600" />
                    Historial de Estados
                  </h3>
                </div>
                <div className="p-5">
                  <StatusHistoryTimeline
                    history={statusHistory}
                    loading={loadingStatusHistory}
                    usersById={usersById}
                  />
                </div>
              </div>
            )}

            <DeliveryDateInfo selectedOrder={selectedOrder} />

            {/* Factura Observations Section */}
            {profileStore.role !== "transportista" && (
              <EditableNotesField
                title="Observaciones para factura"
                value={selectedOrder.factura_observations || ""}
                editedValue={editedFacturaObservations}
                onEditedValueChange={setEditedFacturaObservations}
                isEditing={isEditingFacturaObservations}
                setIsEditing={setIsEditingFacturaObservations}
                isSaving={savingFacturaObservations}
                onSave={updateFacturaObservations}
                onCancel={() => {
                  setIsEditingFacturaObservations(false);
                  setEditedFacturaObservations(
                    selectedOrder.factura_observations || ""
                  );
                }}
                canEdit={true}
                emptyText="No hay observaciones para la factura."
                helpText="Esta información se utilizará para la generación de facturas"
                placeholder="Añadir observaciones para la factura..."
                maxLength={FACTURA_OBSERVATIONS_MAX_LENGTH}
              />
            )}

            {/* Internal Notes Section - hidden from sellers and transportistas */}
            {profileStore.role !== "transportista" &&
              profileStore.role !== "seller" && (
                <EditableNotesField
                  title="Notas internas"
                  value={selectedOrder.internal_notes || ""}
                  editedValue={editedInternalNotes}
                  onEditedValueChange={setEditedInternalNotes}
                  isEditing={isEditingInternalNotes}
                  setIsEditing={setIsEditingInternalNotes}
                  isSaving={savingInternalNotes}
                  onSave={updateInternalNotes}
                  onCancel={() => {
                    setIsEditingInternalNotes(false);
                    setEditedInternalNotes(selectedOrder.internal_notes || "");
                  }}
                  canEdit={
                    profileStore.id === selectedOrder.createdBy ||
                    profileStore.role === "superadmin" ||
                    profileStore.role === "facturacion" ||
                    (profileStore.role === "cobranza" &&
                      selectedOrder.status === "payment_to_check")
                  }
                  emptyText="No hay notas internas para esta nota de venta."
                  helpText="Esta información es solo para uso interno y no es visible para el cliente"
                  placeholder="Añadir notas internas..."
                />
              )}

            {/* Telecheque ID Section - only show if TELECHEQUE condition and pending status */}
            {shouldShowTelechequeInput && (
              <TelechequeIdCard
                selectedOrder={selectedOrder}
                isEditing={isEditingTelechequeId}
                setIsEditing={setIsEditingTelechequeId}
                editedValue={editedTelechequeId}
                setEditedValue={setEditedTelechequeId}
                isSaving={savingTelechequeId}
                onSave={updateTelechequeId}
              />
            )}
          </RevealGroup>
        </ScrollArea>

        {/* Pricing Approval Actions - shown only for pending_pricing status */}
        <PricingApprovalActions
          selectedOrder={selectedOrder}
          clientName={
            clientNames[selectedOrder.clientId] || "Cliente desconocido"
          }
          onOrderStatusChange={onOrderStatusChange}
          onCloseOrderDialog={() => setSelectedOrder(null)}
        />

        {/* General Approval Actions - hidden for pending_pricing since it has its own component */}
        <div className="mt-4 border-t pt-4">
          <ApproveRejectActions
            canApproveOrReject={canApproveOrReject}
            isPending={
              selectedOrder.status === "pending" ||
              selectedOrder.status === "payment_to_check"
            }
            onApprove={() => setIsApproveSheetOpen(true)}
            onReject={() => setIsRejectSheetOpen(true)}
            clientRiskChecked={clientRiskChecked}
            approveText={
              selectedOrder.status === "pending"
                ? "Aprobar Crédito"
                : selectedOrder.status === "payment_to_check"
                  ? "Validar Pago"
                  : "Aprobar"
            }
          />
        </div>

        {/* Status Action Buttons */}
        <StatusActionButtons
          selectedOrder={selectedOrder}
          profileStore={profileStore}
          payments={payments || []}
          localItems={localItems}
          canApproveDocument={canApproveDocument}
          canMarkAsPaid={canMarkAsPaid}
          isAdmin={isAdmin}
          canModifyDispatchOrder={canModifyDispatchOrder}
          markAsTest={markAsTest}
          markAsDelivered={markAsDelivered}
          revertToInTransit={revertToInTransit}
          markAsPaid={markAsPaid}
          markAsUnpaid={markAsUnpaid}
          handleForceApproveWithoutPaymentValidation={
            handleForceApproveWithoutPaymentValidation
          }
          setIsTransportistaRejectSheetOpen={setIsTransportistaRejectSheetOpen}
          onOrderStatusChange={onOrderStatusChange}
          onCloseOrderDialog={() => setSelectedOrder(null)}
        />
      </div>
    );
  };

  return (
    <>
      {useDrawer ? (
        // Drawer (Sheet) implementation
        <Sheet
          open={!!selectedOrder}
          onOpenChange={(open) => {
            if (!open) {
              // Delay unmount to allow animations and async cleanup to complete
              // This prevents NotFoundError on iOS when DOM is manipulated during unmount
              requestAnimationFrame(() => {
                setSelectedOrder(null);
              });
            }
          }}
        >
          <SheetContent
            side="right"
            className="flex w-full flex-col overflow-auto p-4 sm:w-[90vw] sm:max-w-[650px] sm:p-6"
          >
            {renderContent()}
          </SheetContent>
        </Sheet>
      ) : (
        // Traditional modal (Dialog) implementation
        <Dialog
          open={!!selectedOrder}
          onOpenChange={(open) => {
            if (!open) {
              // Delay unmount to allow animations and async cleanup to complete
              requestAnimationFrame(() => {
                setSelectedOrder(null);
              });
            }
          }}
        >
          <DialogContent className="flex max-h-[90vh] max-w-[95vw] flex-col rounded-md p-3 sm:max-w-[700px] sm:rounded-2xl sm:p-6 md:max-h-[80vh]">
            {renderContent()}
          </DialogContent>
        </Dialog>
      )}

      <OrderDetailDialogSheets
        isApproveSheetOpen={isApproveSheetOpen}
        setIsApproveSheetOpen={setIsApproveSheetOpen}
        isRejectSheetOpen={isRejectSheetOpen}
        setIsRejectSheetOpen={setIsRejectSheetOpen}
        rejectionReason={rejectionReason}
        setRejectionReason={setRejectionReason}
        customReason={customReason}
        setCustomReason={setCustomReason}
        isSubmitting={isSubmitting}
        selectedOrder={selectedOrder}
        clientName={clientNames[selectedOrder.clientId]}
        seller={seller}
        handleApprove={handleApprove}
        handleReject={handleReject}
        requiresPrepayment={requiresPrepayment}
      />

      {/* Transportista Reject Sheet */}
      <TransportistaRejectSheet
        isOpen={isTransportistaRejectSheetOpen}
        setIsOpen={setIsTransportistaRejectSheetOpen}
        rejectionReason={transportistaRejectionReason}
        setRejectionReason={setTransportistaRejectionReason}
        isSubmitting={isSubmitting}
        onReject={handleTransportistaReject}
      />

      {/* CreateDispatchGuideDialog */}
      <CreateDispatchGuideDialog
        isOpen={showCreateDispatchGuideDialog}
        onClose={() => setShowCreateDispatchGuideDialog(false)}
        onConfirm={handleCreateDispatchGuide}
        selectedOrder={selectedOrder}
        isCreating={isCreatingDispatchGuide === selectedOrder.id}
      />
    </>
  );
};
